import { Configuration } from '../runtime';
import type { Middleware, ResponseContext } from '../runtime';

// This will be set by the app
let tokenGetter: (() => string | null) | undefined;
let tokenRefresher: (() => Promise<string | null>) | undefined;
let apiBasePathOverride: string | undefined;

/**
 * Set the function to get the access token
 * Call this from your app after initializing auth
 */
export const setTokenGetter = (getter: () => string | null) => {
  tokenGetter = getter;
};

/**
 * Set the function to refresh the access token
 * Call this from your app after initializing auth
 */
export const setTokenRefresher = (refresher: () => Promise<string | null>) => {
  tokenRefresher = refresher;
};

export const setApiBasePath = (basePath: string | undefined) => {
  apiBasePathOverride = basePath ? basePath.replace(/\/$/, '') : undefined;
};

/**
 * Middleware to handle token refresh on 401 errors
 */
class TokenRefreshMiddleware implements Middleware {
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  async post(context: ResponseContext): Promise<Response | void> {
    // If we get a 401 and we have a refresh function, try to refresh the token
    if (context.response.status === 401 && tokenRefresher) {
      // Prevent multiple simultaneous refresh attempts
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshPromise = tokenRefresher().finally(() => {
          this.isRefreshing = false;
          this.refreshPromise = null;
        });
      }

      const newToken = await this.refreshPromise;
      
      if (newToken) {
        // Retry the original request with the new token
        const originalRequest = context.init;
        const headers = new Headers(originalRequest.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        
        return fetch(context.url, {
          ...originalRequest,
          headers,
        });
      } else {
        console.warn('❌ Token refresh failed');
      }
    }
    
    return context.response;
  }
}

export const getApiConfigNext = async (accessToken?: string): Promise<Configuration> => {
  const token = accessToken || getAccessToken();

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return new Configuration({
    basePath: getNextBasePath(),
    headers,
    middleware: [new TokenRefreshMiddleware()],
  });
};

export const getNextBasePath = (): string => {
  if (apiBasePathOverride) {
    return apiBasePathOverride;
  }

  if (typeof window === 'undefined') {
    return 'http://backend-next:8080/api-next';
  }

  const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL as string | undefined;

  if (baseUrl) {
    const browserHost = window.location.hostname;
    const isBrowserLocal = browserHost === 'localhost' || browserHost === '127.0.0.1';
    const isLocalApiBase = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/api\/?$/.test(baseUrl);

    if (isLocalApiBase && !isBrowserLocal) {
      return '/api-next';
    }

    if (/\/api\/?$/.test(baseUrl)) {
      return baseUrl.replace(/\/api\/?$/, '/api-next');
    }

    return `${baseUrl.replace(/\/$/, '')}/api-next`;
  }

  return '/api-next';
};

/**
 * Get access token from storage or auth provider
 */
export const getAccessToken = (): string | null => {
  if (tokenGetter) {
    return tokenGetter();
  }
  // Fallback to localStorage (only in browser)
  if (typeof window !== 'undefined') {
    return localStorage.getItem('access_token');
  }
  return null;
};
